import { HttpService } from "../http/HttpService";
import { AbstractAdminEntityService } from "./AbstractAdminEntityService";
import { PlayerCashbackOverride, PlayerCashbackOverrideListResponse, SetPlayerCashbackOverrideRequest } from "../http/protocol";

export interface PlayerCashbackOverrideListRequest {
  userId?: number;
  configId?: number;
  includeExpired?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  ascending?: boolean;
}

// expiresAt is a numeric epoch-millis wire value end to end: admin-rest's JAX-RS layer reads the
// request body and writes the response in millis (the convention the date picker, new Date(ms) and
// the table's ColumnType.DATE all use), and translates to/from the cashback service's epoch-seconds
// representation on its own. The generated protocol type says Date for this field — a known protocol
// mismatch — so it is carried as a number and cast at the boundary, with no scaling here.
export class PlayerCashbackOverrideService extends AbstractAdminEntityService<PlayerCashbackOverride, PlayerCashbackOverrideListResponse> {
  constructor(http: HttpService) {
    super("/cashback/override", http);
  }

  listPaged<T extends PlayerCashbackOverrideListRequest>(listRequest: T): Promise<PlayerCashbackOverrideListResponse> {
    const params = new URLSearchParams();
    if (listRequest.userId != null) {
      params.set("userId", String(listRequest.userId));
    }
    if (listRequest.configId != null) {
      params.set("configId", String(listRequest.configId));
    }
    params.set("includeExpired", String(listRequest.includeExpired ?? false));
    params.set("limit", String(listRequest.limit ?? 20));
    params.set("offset", String(listRequest.offset ?? 0));
    params.set("orderBy", listRequest.orderBy ?? "id");
    params.set("ascending", String(listRequest.ascending ?? true));
    return this.http.get<PlayerCashbackOverrideListResponse>(`${this.entityPath}/list?${params.toString()}`);
  }

  // Override is always an upsert keyed on (userId, configId), so there is no create-vs-update branch.
  save(request: SetPlayerCashbackOverrideRequest): Promise<PlayerCashbackOverride> {
    return this.http.put<PlayerCashbackOverride, string>(this.entityPath, JSON.stringify(request));
  }

  removeOverride(userId: number, configId: number): Promise<{}> {
    return this.http.delete(`${this.entityPath}/${userId}/${configId}`);
  }
}
